import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getTorneosPublic, getTorneoPublic } from "services/publicTorneosService";
import { mapToViewModel } from "components/torneos/torneoMapper";
import PublicPickScreen from "./PublicPickScreen";
import PublicResultsScreen from "./PublicResultsScreen";
import { usePageLoading } from "hooks/usePageLoading";
import { useRefrescoResultados } from "hooks/useRefrescoResultados";
import { usePullToRefresh } from "hooks/usePullToRefresh";
import { reanclarNav } from "./reanclarNav";
import { etiquetaFecha, numerosDeFecha, ordenarFechasDesc } from "components/torneos/fechas";

// Sección PÚBLICA de torneos (sin login): los jugadores navegan
// año → torneo → rueda → zona → fecha y llegan a ver la tabla de posiciones
// y los partidos. Es SOLO LECTURA: no se carga ni se modifica nada.
// La lógica de navegación replica la de components/resultados/resultados.jsx,
// pero sin auto-guardado ni edición.
//
// Estando en la pantalla de resultados el torneo se vuelve a traer solo cuando
// alguien carga un resultado desde el admin (ver hooks/useRefrescoResultados).

export default function TorneosPublico(props) {
  const doneLoading = usePageLoading(props);
  const [loading, setLoading] = useState(true);
  const [torneos, setTorneos] = useState([]); // registros crudos (backend)
  const [torneo, setTorneo] = useState(null); // torneo seleccionado (modelo front)
  const [nav, setNav] = useState([{ level: "years" }]);
  const [version, setVersion] = useState(null); // `updated` del torneo que está en pantalla

  const current = nav[nav.length - 1];

  // El refresco necesita el torneo y el stack de antes del refetch para reanclar
  // la navegación, y corre fuera del ciclo de render: van por ref.
  const torneoRef = useRef(null);
  torneoRef.current = torneo;
  const navRef = useRef(nav);
  navRef.current = nav;

  const years = useMemo(
    () => [...new Set(torneos.map((t) => t.ano).filter((y) => y != null))].sort((a, b) => b - a),
    [torneos]
  );
  const torneosForYear = (ano) =>
    torneos.filter((t) => t.ano === ano).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));

  const loadTorneoInto = async (id) => {
    const rec = await getTorneoPublic(id);
    if (rec && rec._id) {
      setTorneo(mapToViewModel(rec));
      setVersion(rec.updated || null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await getTorneosPublic();
        const arr = Array.isArray(list) ? list : [];
        setTorneos(arr);
        const ys = [...new Set(arr.map((t) => t.ano).filter((y) => y != null))].sort((a, b) => b - a);
        // Si hay un solo año (y un solo torneo) saltamos esos pasos.
        if (ys.length === 1) {
          const ano = ys[0];
          const forYear = arr
            .filter((t) => t.ano === ano)
            .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
          if (forYear.length === 1) {
            await loadTorneoInto(forYear[0]._id);
            setNav([{ level: "ruedas", ano, torneoId: forYear[0]._id }]);
          } else {
            setNav([{ level: "torneos", ano }]);
          }
        }
      } catch (error) {
        console.error("Error cargando torneos:", error);
      } finally {
        setLoading(false);
        doneLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El back del navegador/celular hace pop del stack.
  useEffect(() => {
    const onPop = () => setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const push = (frame) => {
    setNav((s) => [...s, frame]);
    window.history.pushState({}, "");
  };
  const goBack = () => window.history.back();

  // Vuelve a traer el torneo sin sacar al usuario de donde está.
  // mapToViewModel regenera los ids de rueda/zona/fecha en cada mapeo, así que el
  // stack de navegación hay que reanclarlo contra el árbol nuevo antes de
  // reemplazar el torneo. Devuelve la versión que quedó cargada.
  const refrescarTorneo = useCallback(async () => {
    const anterior = torneoRef.current;
    if (!anterior || !anterior._id) return null;
    const rec = await getTorneoPublic(anterior._id);
    if (!rec || !rec._id) return null;
    const nuevo = mapToViewModel(rec);
    setNav(reanclarNav(navRef.current, anterior, nuevo));
    setTorneo(nuevo);
    setVersion(rec.updated || null);
    return rec.updated || null;
  }, []);

  // ---- Selectores derivados ----
  const round = torneo && torneo.ruedas.find((r) => r.id === current.roundId);
  const zone = round && round.zones.find((z) => z.id === current.zoneId);
  const date = zone && zone.matchDates.find((d) => d.id === current.dateId);

  // Guarda de entidad huérfana (si el stack apunta a algo que ya no existe).
  useEffect(() => {
    if (!torneo) return;
    const orphan =
      (current.roundId && !round) || (current.zoneId && !zone) || (current.dateId && !date);
    if (orphan) setNav((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, [current, torneo, round, zone, date]);

  // Solo se poletea estando en los resultados: en las pantallas de selección no
  // hay nada que se pueda desactualizar.
  const enResultados = current.level === "results";
  const { refrescarAhora, refrescando } = useRefrescoResultados({
    torneoId: torneo && torneo._id,
    activo: enResultados,
    date,
    versionInicial: version,
    onRefrescar: refrescarTorneo,
  });

  // Tirar la pantalla hacia abajo para recargar, como en las apps del celular.
  // La única señal visual es el contenido que acompaña al dedo y el botón de
  // refrescar del header, que gira mientras trae los datos.
  const { distancia, refrescando: tirando } = usePullToRefresh({
    activo: enResultados,
    onRefresh: refrescarAhora,
  });

  const selectTorneo = async (id) => {
    await loadTorneoInto(id);
    push({ level: "ruedas", ano: current.ano, torneoId: id });
  };

  const renderScreen = () => {
    switch (current.level) {
      case "torneos":
        return (
          <PublicPickScreen
            title="Elegí el torneo"
            subtitle={"Año " + current.ano}
            options={torneosForYear(current.ano).map((t) => ({ key: t._id, label: t.nombre }))}
            emptyText="No hay torneos para este año."
            onSelect={selectTorneo}
            onBack={years.length > 1 ? goBack : undefined}
          />
        );
      case "ruedas":
        if (!torneo) return null;
        return (
          <PublicPickScreen
            title="Elegí la rueda"
            subtitle={torneo.nombre}
            options={torneo.ruedas.map((r) => ({ key: r.id, label: r.name }))}
            emptyText="El torneo no tiene ruedas."
            onSelect={(roundId) => push({ ...current, level: "zonas", roundId })}
            onBack={goBack}
          />
        );
      case "zonas":
        if (!round) return null;
        return (
          <PublicPickScreen
            title="Elegí la zona"
            subtitle={round.name}
            options={round.zones.map((z) => ({ key: z.id, label: z.name }))}
            emptyText="La rueda no tiene zonas."
            onSelect={(zoneId) => push({ ...current, level: "fechas", zoneId })}
            onBack={goBack}
          />
        );
      case "fechas": {
        if (!zone) return null;
        const numeros = numerosDeFecha(zone.matchDates);
        const fechasDesc = ordenarFechasDesc(zone.matchDates);
        return (
          <PublicPickScreen
            title="Elegí la fecha"
            subtitle={zone.name}
            options={fechasDesc.map((f) => ({ key: f.id, label: etiquetaFecha(f, numeros[f.id]) }))}
            emptyText="La zona no tiene fechas."
            onSelect={(dateId) => push({ ...current, level: "results", dateId })}
            onBack={goBack}
          />
        );
      }
      case "results":
        if (!zone || !date) return null;
        return (
          <PublicResultsScreen
            zone={zone}
            date={date}
            teams={torneo.equipos}
            headerTitle={etiquetaFecha(date, numerosDeFecha(zone.matchDates)[date.id])}
            onBack={goBack}
            onRefresh={refrescarAhora}
            refrescando={refrescando}
          />
        );
      case "years":
      default:
        return (
          <PublicPickScreen
            title="Elegí el año"
            options={years.map((y) => ({ key: y, label: String(y) }))}
            emptyText="Todavía no hay torneos cargados."
            onSelect={(ano) => push({ level: "torneos", ano })}
          />
        );
    }
  };

  // Mientras el dedo arrastra, el contenido acompaña sin transición; al soltar,
  // vuelve animado.
  const estiloArrastre = {
    transform: distancia ? "translateY(" + distancia + "px)" : undefined,
    transition: distancia && !tirando ? "none" : "transform 0.22s ease",
  };

  return (
    <div className="tpub">
      <div className="tpub-shell">
        <div style={estiloArrastre}>
          {loading ? (
            <p className="tpub-loading">Cargando…</p>
          ) : years.length === 0 && torneos.length === 0 ? (
            <p className="tpub-loading">No hay torneos disponibles.</p>
          ) : (
            renderScreen()
          )}
        </div>
      </div>
    </div>
  );
}
